import { type ReadonlyVec2 } from 'gl-matrix';

import { getArrowheadFromPathCap } from '@canvas/arrowhead-definitions';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';
import { getGraphicBoundingBox } from '@canvas/graphics/bounds';
import { boxCreate, boxExpandToFitBox, type Box } from '@canvas/math/box';
import { positionAndSizeVec2ToBox } from '@canvas/math/box/convert';
import {
  PathCap,
  pathCapToBoolean,
  strokeToThickness,
  strokeToArrowheadSizeMultiplier,
  type PathStroke,
} from '@canvas/model/elements/path';
import { getBoundingBoxForPathSegments, type PathSegment } from '@canvas/path';

import {
  createArrowheadSpatialData,
  getSpatialDataForArrowheadOnPath,
} from './get-spatial-data-for-arrowhead-on-path';

const arrowheadSpatialDataFlyweight = createArrowheadSpatialData();
const arrowheadBoxFlyweight = boxCreate();

/**
 * Gets the bounding box of a path entity including arrowheads.
 *
 * @param start the start point of the path
 * @param segments the path's segments
 * @param stroke the stroke setting for the path
 * @param startCap the optional start cap on the path
 * @param endCap the optional end cap on the path
 * @returns
 */
export function getBoundingBoxForPath(
  start: ReadonlyVec2,
  segments: PathSegment[],
  stroke: PathStroke,
  startCap?: PathCap,
  endCap?: PathCap
): Box {
  const thickness = strokeToThickness[stroke];

  const isDiagrammingArrowheadsEnabled = getExperimentValue(
    'diagrammingArrowheads',
    'enabled',
    { fireExperimentExposure: false }
  );

  if (!isDiagrammingArrowheadsEnabled) {
    return getBoundingBoxForPathSegments(start, segments, thickness, {
      startCap: pathCapToBoolean(startCap),
      endCap: pathCapToBoolean(endCap),
      stroke,
    });
  }

  // create initial bounding box from path segments
  const boundingBox = getBoundingBoxForPathSegments(start, segments, thickness);

  // expand bounding box based on arrowheads if present
  for (let i = 0; i < 2; i++) {
    const isStart = i === 0;
    const pathCap = isStart ? startCap : endCap;
    if (pathCap === undefined || pathCap === PathCap.NONE) {
      continue;
    }

    const { naturalSize, offsetFromEnd, graphic } = getArrowheadFromPathCap(pathCap);

    const sizeMultiplier = strokeToArrowheadSizeMultiplier[stroke];
    const scaledNaturalSize: ReadonlyVec2 = [
      naturalSize[0] * sizeMultiplier,
      naturalSize[1] * sizeMultiplier,
    ];
    const scaledOffsetFromEnd = offsetFromEnd * sizeMultiplier;

    // get spatial data for arrowheads and write it to arrowheadSpatialDataFlyweight
    getSpatialDataForArrowheadOnPath(
      start,
      segments,
      isStart ? 0 : 1,
      scaledNaturalSize,
      scaledOffsetFromEnd,
      !isStart,
      arrowheadSpatialDataFlyweight
    );

    // convert that to a box
    positionAndSizeVec2ToBox(
      arrowheadSpatialDataFlyweight.position,
      arrowheadSpatialDataFlyweight.size,
      arrowheadBoxFlyweight
    );

    // use this to get the bounding box of the graphics content
    const graphicsBoundingBox = getGraphicBoundingBox(
      graphic,
      arrowheadBoxFlyweight,
      arrowheadSpatialDataFlyweight.rotation,
      undefined, // use box centre point as rotation origin
      thickness
    );

    // expand the path elements boundingBox to include the arrowheads box
    if (graphicsBoundingBox) {
      boxExpandToFitBox(boundingBox, graphicsBoundingBox);
    }
  }

  return boundingBox;
}
